import * as React from 'react';
import { Box, Link, useMediaQuery, useTheme } from "@mui/material";
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { tokens, WIDE_LAYOUT_QUERY } from '../../../Components/DashboardTheme/DashboardTheme';
import { Kicker } from '../../../Components/Typography/Typography';
import Reveal from '../../../Components/Reveal/Reveal';
import { FightersData } from './FightersData';

export default function FightsHistoryPage() {
  const theme = useTheme();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isWide = useMediaQuery(WIDE_LAYOUT_QUERY);

  const columns = isScreenSmall ? 1 : isWide ? 3 : 2;

  return (
    <Box sx={{ backgroundColor: tokens.ink, minHeight: '100vh' }}>
      {/* ---------- Header ---------- */}
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 3, md: 6 },
          pt: { xs: 14, md: 20 },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Reveal>
          <Kicker justifyContent="flex-start">Forti e fieri</Kicker>
          <Box
            component="h1"
            sx={{
              m: 0,
              mt: 2,
              fontFamily: tokens.display,
              fontSize: { xs: 52, sm: 80, md: 116 },
              lineHeight: 0.9,
              color: tokens.white,
            }}
          >
            STORIA DEL TEAM
          </Box>
          <Box
            sx={{
              fontFamily: tokens.heading,
              fontSize: { xs: 13, md: 15 },
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: tokens.dim,
              mt: 2.5,
            }}
          >
            {FightersData.length} atleti · Vivenzio Gym
          </Box>
        </Reveal>
      </Box>

      {/* ---------- Roster ---------- */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: { xs: 2, md: 2.5 },
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 3, md: 6 },
          pb: { xs: 8, md: 14 },
        }}
      >
        {FightersData.map((fighter, i) => {
          const achievements = Object.values(fighter.descriptions || {});
          const hasLink = Boolean(fighter.link);

          return (
            <Reveal key={fighter.id} delay={(i % columns) * 90}>
              <Box
                component={hasLink ? Link : 'div'}
                {...(hasLink
                  ? { href: fighter.link, target: '_blank', rel: 'noopener noreferrer', underline: 'none' }
                  : {})}
                sx={{
                  display: 'block',
                  position: 'relative',
                  height: '100%',
                  border: `1px solid ${tokens.line}`,
                  backgroundColor: tokens.surface,
                  overflow: 'hidden',
                  transition: `all 0.5s ${tokens.ease}`,
                  ':hover': hasLink
                    ? { borderColor: tokens.red, transform: 'translateY(-4px)' }
                    : {},
                  ':hover img': { transform: 'scale(1.06)', filter: 'grayscale(0%) brightness(0.95)' },
                }}
              >
                <Box sx={{ position: 'relative', aspectRatio: '3 / 4', overflow: 'hidden' }}>
                  <Box
                    component="img"
                    src={fighter.img}
                    alt={fighter.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center 20%',
                      filter: 'grayscale(55%) brightness(0.8)',
                      transition: `all 1s ${tokens.ease}`,
                    }}
                  />
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: `linear-gradient(180deg, transparent 45%, ${tokens.surface} 100%)`,
                    }}
                  />
                  {hasLink && (
                    <Box sx={{ position: 'absolute', top: 14, right: 14, color: tokens.white, display: 'flex' }}>
                      <ArrowOutwardIcon fontSize="small" />
                    </Box>
                  )}
                </Box>

                <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Box
                    sx={{
                      fontFamily: tokens.display,
                      fontSize: { xs: 28, md: 32 },
                      lineHeight: 1,
                      color: tokens.white,
                    }}
                  >
                    {fighter.name}
                  </Box>
                  <Box aria-hidden sx={{ width: 36, height: 2, backgroundColor: tokens.red, my: 2 }} />

                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
                    {/* Some records repeat a line verbatim, so index into the key. */}
                    {achievements.map((achievement, achievementIndex) => (
                      <Box
                        component="li"
                        key={`${fighter.id}-${achievementIndex}`}
                        sx={{
                          fontFamily: tokens.body,
                          fontWeight: 300,
                          fontSize: 13.5,
                          lineHeight: 1.55,
                          color: tokens.muted,
                          pl: 2,
                          position: 'relative',
                          '::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 8,
                            width: 5,
                            height: 5,
                            backgroundColor: tokens.red,
                          },
                        }}
                      >
                        {achievement}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Reveal>
          );
        })}
      </Box>
    </Box>
  );
}
